import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Panel from "../components/common/Panel";
import Seo from "../components/common/Seo";
import { findPostById } from "../data/blogPosts";
import { t } from "../i18n/messages";
import { buildPath } from "../utils/routing";
import { localeContent } from "../utils/localeContent";

export default function BlogPostPage({ locale }) {
  const { postId } = useParams();
  const post = findPostById(postId);
  const content = localeContent(post?.locales, locale);
  const siteOrigin = import.meta.env.VITE_SITE_ORIGIN || "https://chronos-arte-node.example";

  if (!post || !content) {
    return (
      <Panel title={t(locale, "blogPostNotFound")} description={t(locale, "blogPostNotFoundDesc")}>
        <Link className="btn btn-primary" to={buildPath(locale, "blog")}>{t(locale, "blogBackToList")}</Link>
      </Panel>
    );
  }

  return (
    <>
      <Seo
        locale={locale}
        title={`${content.title} | Chronos Arte Node`}
        description={content.excerpt}
        path={buildPath(locale, `blog/${post.id}`)}
      />
      <Helmet>
        {Array.isArray(post.keywords) && post.keywords.length ? (
          <meta name="keywords" content={post.keywords.join(", ")} />
        ) : null}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: content.title,
            description: content.excerpt,
            inLanguage: locale,
            keywords: post.keywords || [],
            datePublished: post.publishDate,
            dateModified: post.updatedDate,
            author: { "@type": "Person", name: post.author },
            publisher: { "@type": "Organization", name: "Chronos Arte Node" },
            mainEntityOfPage: `${siteOrigin}${buildPath(locale, `blog/${post.id}`)}`,
          })}
        </script>
      </Helmet>

      <Panel className="blog-post-panel" title={content.title} description={content.excerpt}>
        <div className="blog-atmosphere" aria-hidden="true">
          <span className="blog-orb blog-orb-one" />
          <span className="blog-orb blog-orb-two" />
        </div>

        <div className="blog-post-shell">
          <div className="blog-post-topic-row">
            <span className="pill blog-topic-pill">{post.topic}</span>
          </div>

          <div className="meta-row blog-meta-row">
            <span className="blog-meta-chip">{post.publishDate}</span>
            <span className="blog-meta-chip">{post.updatedDate}</span>
            <span className="blog-meta-chip">{post.author}</span>
            <span className="blog-meta-chip">{post.readMinutes} {t(locale, "blogMinutes")}</span>
          </div>

          <p className="blog-post-lead">{content.excerpt}</p>
        </div>

        <div className="article-body blog-article-body">
          {(content.sections || []).map((section, index) => (
            <section className={`blog-section ${index % 2 ? "is-alt" : ""}`} key={section.heading}>
              <h3>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {section.heading}
              </h3>
              {section.paragraphs.map((text, idx) => (
                <p key={`${section.heading}-${idx}`}>{text}</p>
              ))}
            </section>
          ))}
        </div>

        <div className="tool-row blog-tool-row">
          <Link className="btn btn-ghost" to={buildPath(locale, "blog")}>
            {t(locale, "blogBackToList")}
          </Link>
        </div>
      </Panel>
    </>
  );
}
